/**
 * Ekster — Tracker Card template
 * Generic tab / toggle controller shared by the ek-tc-* sections.
 *
 * Markup contract:
 *   [data-ek-tc-tabs]                  wrapper (one controller per wrapper)
 *   [data-ek-tc-tab="<key>"]           button, sets the active key
 *   [data-ek-tc-panel="<key>"]         panel, shown when its key is active
 *   [data-ek-tc-panel="<key> <key2>"]  panel visible for multiple keys
 *
 * Active state is expressed with the data-active attribute so all styling
 * stays in CSS and a single attribute write switches the whole group.
 */
(function () {
  'use strict';

  var WRAPPER = '[data-ek-tc-tabs]';

  function panelKeys(panel) {
    return (panel.getAttribute('data-ek-tc-panel') || '').split(/\s+/).filter(Boolean);
  }

  function activate(wrapper, key, focusPanel) {
    if (!key) return;

    var buttons = wrapper.querySelectorAll('[data-ek-tc-tab]');
    var panels = wrapper.querySelectorAll('[data-ek-tc-panel]');
    var i;

    for (i = 0; i < buttons.length; i++) {
      var isCurrent = buttons[i].getAttribute('data-ek-tc-tab') === key;
      if (isCurrent) {
        buttons[i].setAttribute('data-active', '');
      } else {
        buttons[i].removeAttribute('data-active');
      }
      buttons[i].setAttribute('aria-selected', isCurrent ? 'true' : 'false');
      buttons[i].setAttribute('tabindex', isCurrent ? '0' : '-1');
    }

    // Scroll mode keeps every panel on screen and only moves the track, so it
    // must never touch `hidden`: Tailwind preflight applies
    // [hidden] { display: none !important } and CSS cannot undo it.
    var scrollMode = wrapper.hasAttribute('data-ek-tc-scroll');

    for (i = 0; i < panels.length; i++) {
      var visible = panelKeys(panels[i]).indexOf(key) !== -1;
      if (visible) {
        panels[i].setAttribute('data-active', '');
      } else {
        panels[i].removeAttribute('data-active');
      }
      if (!scrollMode) {
        if (visible) {
          panels[i].removeAttribute('hidden');
        } else {
          panels[i].setAttribute('hidden', 'hidden');
        }
      } else {
        panels[i].removeAttribute('hidden');
      }
    }

    wrapper.setAttribute('data-ek-tc-active', key);

    // Wrappers marked data-ek-tc-scroll keep every panel visible and use the
    // tabs to scroll the track instead of hiding the other panels.
    if (wrapper.hasAttribute('data-ek-tc-scroll')) {
      var target = wrapper.querySelector('[data-ek-tc-panel~="' + key + '"]');
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }

    if (focusPanel) {
      var current = wrapper.querySelector('[data-ek-tc-tab][data-active]');
      if (current) current.focus();
    }
  }

  function keyboardNav(wrapper, event) {
    var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (keys.indexOf(event.key) === -1) return;

    var buttons = Array.prototype.slice.call(wrapper.querySelectorAll('[data-ek-tc-tab]'));
    if (buttons.length < 2) return;

    var currentIndex = buttons.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    var nextIndex = currentIndex;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % buttons.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = buttons.length - 1;

    event.preventDefault();
    activate(wrapper, buttons[nextIndex].getAttribute('data-ek-tc-tab'), true);
  }

  function init(wrapper) {
    if (!wrapper || wrapper.hasAttribute('data-ek-tc-ready')) return;
    wrapper.setAttribute('data-ek-tc-ready', '');

    wrapper.addEventListener('click', function (event) {
      var button = event.target.closest ? event.target.closest('[data-ek-tc-tab]') : null;
      if (!button || !wrapper.contains(button)) return;
      event.preventDefault();
      activate(wrapper, button.getAttribute('data-ek-tc-tab'), false);
    });

    wrapper.addEventListener('keydown', function (event) {
      keyboardNav(wrapper, event);
    });

    var initial =
      wrapper.getAttribute('data-ek-tc-active') ||
      (wrapper.querySelector('[data-ek-tc-tab][data-active]') || {}).dataset;

    if (typeof initial === 'object' && initial !== null) initial = initial.ekTcTab;
    if (!initial) {
      var first = wrapper.querySelector('[data-ek-tc-tab]');
      initial = first ? first.getAttribute('data-ek-tc-tab') : null;
    }

    activate(wrapper, initial, false);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var wrappers = scope.querySelectorAll(WRAPPER);
    for (var i = 0; i < wrappers.length; i++) init(wrappers[i]);
    if (scope !== document && scope.matches && scope.matches(WRAPPER)) init(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    var wrapper = event.target.closest ? event.target.closest(WRAPPER) : null;
    var panel = event.target.closest ? event.target.closest('[data-ek-tc-panel]') : null;
    if (wrapper && panel) activate(wrapper, panelKeys(panel)[0], false);
  });
})();
